import type { Point } from '../core/types';

/** Called for every stamp the walker lays down; `direction` is in radians. */
export type DabEmitter = (point: Point, direction: number) => void;

/**
 * Turns a stream of pointer positions into evenly spaced stamps. Photopea puts it
 * plainly: a stroke has no lines in it, only copies of the tip placed along the
 * movement, and the gap between them is the spacing.
 *
 * The leftover distance carries across segments, so slow and fast strokes get the
 * same spacing and a stroke never restarts its rhythm on each pointer event.
 */
export class SpacingWalker {
  private last: Point | null = null;
  private remaining = 0;
  private direction = 0;

  /**
   * Starts a stroke: the first stamp lands exactly on the pointer, unless
   * `emitFirst` is false — a Shift+click segment starts on a dab the previous
   * stroke already laid down, and stamping it twice darkens the joint.
   */
  begin(point: Point, step: number, emit: DabEmitter, emitFirst = true): void {
    this.last = { ...point };
    this.remaining = Math.max(0.5, step);
    this.direction = 0;
    if (emitFirst) emit(point, 0);
  }

  extend(point: Point, step: number, emit: DabEmitter): void {
    if (!this.last) {
      this.begin(point, step, emit);
      return;
    }
    const dx = point.x - this.last.x;
    const dy = point.y - this.last.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 1e-4) return;

    this.direction = Math.atan2(dy, dx);
    const safeStep = Math.max(0.5, step);
    let travelled = 0;
    while (this.remaining <= distance - travelled) {
      travelled += this.remaining;
      const t = travelled / distance;
      emit({ x: this.last.x + dx * t, y: this.last.y + dy * t }, this.direction);
      this.remaining = safeStep;
    }
    this.remaining -= distance - travelled;
    this.last = { ...point };
  }

  get currentDirection(): number {
    return this.direction;
  }
}

/** Exponential smoothing of the pointer path, as Photoshop's stroke smoothing. */
export class PathSmoother {
  private current: Point | null = null;

  constructor(private readonly amount: number) {}

  reset(point: Point): Point {
    this.current = { ...point };
    return point;
  }

  push(point: Point): Point {
    if (!this.current) return this.reset(point);
    const lag = Math.min(0.9, Math.max(0, this.amount));
    this.current = {
      x: this.current.x + (point.x - this.current.x) * (1 - lag),
      y: this.current.y + (point.y - this.current.y) * (1 - lag),
    };
    return this.current;
  }
}
